import { describePlace, type GeoPlace } from "@/lib/api/models/birth";
import { Button } from "@/ui/Button";
import { Block, ErrorNote } from "@/ui/Field";
import { Screen } from "@/ui/Screen";
import { Display, Mono, Tiny } from "@/ui/Text";

interface PlaceStepProps {
  readonly query: string;
  readonly results: readonly GeoPlace[];
  readonly searching: boolean;
  readonly place: GeoPlace | null;
  readonly error: string | null;
  readonly onQueryChange: (query: string) => void;
  readonly onSelect: (place: GeoPlace) => void;
  readonly onContinue: () => void;
  readonly busy?: boolean;
}

/**
 * Step 3 — birth place. Typeahead over the engine's city dataset; each hit
 * shows its resolved timezone, which is the tell that the calculation is
 * real.
 */
export function PlaceStep({ query, results, searching, place, error, onQueryChange, onSelect, onContinue, busy = false }: PlaceStepProps) {
  return (
    <Screen
      middle
      progress={1}
      foot={<Button label="Show me my week" disabled={place === null} busy={busy} onClick={onContinue} />}
    >
      <Display>Where were you born?</Display>
      <div className="mt-8">
        <label htmlFor="birth-place" className="label mb-2 block">Town or city</label>
        <div className="flex min-h-16 w-full items-center gap-3 border-[1.5px] border-mid px-5 focus-within:border-ink">
          <SearchIcon />
          <input
            id="birth-place"
            type="search"
            name="birth-place"
            autoComplete="off"
            autoCorrect="off"
            enterKeyHint="search"
            placeholder="Type your town or city"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            aria-busy={searching || undefined}
            className="w-full appearance-none border-0 bg-transparent p-0 font-display text-[22px] leading-tight text-ink outline-none placeholder:text-mid md:text-[24px]"
          />
        </div>
      </div>
      <ErrorNote message={error} />
      {place === null && results.length > 0 && (
        <ul className="mt-6 border border-rule" role="listbox" aria-label="Matching places">
          {results.map((result, index) => (
            <li key={`${result.geonames_id}-${index}`} className={index > 0 ? "border-t border-rule" : ""}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => onSelect(result)}
                className="flex min-h-12 w-full items-center justify-between gap-3 px-4 py-3 text-left"
              >
                <span className="text-[16px]">{describePlace(result)}</span>
                <Mono className="shrink-0 text-mid">{result.tz}</Mono>
              </button>
            </li>
          ))}
        </ul>
      )}
      {place && (
        <div className="mt-6">
          <Block label="Birth place">
            <p className="text-[16px]">{describePlace(place)}</p>
            <Mono className="mt-1 block text-mid">
              {place.lat.toFixed(3)}, {place.lon.toFixed(3)} · {place.tz}
            </Mono>
          </Block>
        </div>
      )}
      <Tiny className="mt-3">
        We work out the time zone from the place, using the clock rules of the year you were born.
      </Tiny>
    </Screen>
  );
}

function SearchIcon() {
  return (
    <svg aria-hidden width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="shrink-0 text-ink">
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5 20 20" />
    </svg>
  );
}
